import { motion } from "framer-motion";
import { 
  GripVertical, 
  Video, 
  FileText, 
  Download,
  ChevronRight,
  Plus,
  FolderOpen,
  Eye,
  Layers,
  Type,
} from "lucide-react";
import { Container } from "./shared";

function CurriculumBuilderMockup() {
  const sections = [
    {
      title: "Getting Started",
      lessons: [
        { title: "Welcome to the Course", type: "video", duration: "2:30" },
        { title: "Course Overview", type: "text" },
      ],
    },
    {
      title: "Core Fundamentals",
      lessons: [
        { title: "Understanding the Basics", type: "video", duration: "15:45" },
        { title: "Hands-on Exercise", type: "text" },
        { title: "Resource Files", type: "download" },
      ],
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-xl h-full flex flex-col"
    >
      {/* Header */}
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-emerald-500" />
          <span className="font-semibold text-foreground">Curriculum</span>
        </div>
        <button className="flex items-center gap-1 text-sm text-emerald-500 hover:text-emerald-600">
          <Plus className="w-4 h-4" />
          Add Section
        </button>
      </div>
      
      {/* Sections */}
      <div className="p-4 space-y-4 flex-1">
        {sections.map((section, si) => (
          <motion.div
            key={si}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: si * 0.1, duration: 0.4 }}
            viewport={{ once: true }}
            className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden"
          >
            {/* Section Header */}
            <div className="bg-neutral-50 dark:bg-neutral-800 p-3 flex items-center gap-2">
              <GripVertical className="w-4 h-4 text-neutral-400 cursor-grab" />
              <FolderOpen className="w-4 h-4 text-amber-500" />
              <span className="font-medium text-sm text-foreground flex-1">{section.title}</span>
              <ChevronRight className="w-4 h-4 text-neutral-400" />
            </div>
            
            {/* Lessons */}
            <div className="p-2 space-y-1">
              {section.lessons.map((lesson, li) => (
                <div
                  key={li}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors cursor-pointer group"
                >
                  <GripVertical className="w-4 h-4 text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />
                  <div className={`w-7 h-7 rounded flex items-center justify-center ${
                    lesson.type === 'video' ? 'bg-blue-100 dark:bg-blue-900/30' :
                    lesson.type === 'download' ? 'bg-purple-100 dark:bg-purple-900/30' :
                    'bg-neutral-100 dark:bg-neutral-800'
                  }`}>
                    {lesson.type === 'video' && <Video className="w-3.5 h-3.5 text-blue-500" />}
                    {lesson.type === 'text' && <FileText className="w-3.5 h-3.5 text-neutral-500" />}
                    {lesson.type === 'download' && <Download className="w-3.5 h-3.5 text-purple-500" />}
                  </div>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300 flex-1">{lesson.title}</span>
                  {lesson.duration && (
                    <span className="text-xs text-neutral-400">{lesson.duration}</span>
                  )}
                </div>
              ))}
              
              {/* Add Lesson */}
              <button className="w-full flex items-center gap-2 p-2 rounded-lg border border-dashed border-neutral-300 dark:border-neutral-700 text-neutral-500 hover:border-emerald-500 hover:text-emerald-500 transition-colors text-sm">
                <Plus className="w-4 h-4" />
                Add Lesson
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

function LessonEditorMockup() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      viewport={{ once: true }}
      className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-xl h-full flex flex-col"
    >
      {/* Header */}
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Type className="w-5 h-5 text-emerald-500" />
          <span className="font-semibold text-foreground">Lesson Editor</span>
        </div>
        <button className="flex items-center gap-1 text-sm bg-emerald-500 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-600">
          <Eye className="w-4 h-4" />
          Preview
        </button>
      </div>
      
      {/* Editor Toolbar */}
      <div className="p-2 border-b border-neutral-200 dark:border-neutral-800 flex items-center gap-1 overflow-x-auto">
        {['B', 'I', 'U', '|', 'H1', 'H2', '|', '•', '1.', '|', '🔗', '📷', '🎬'].map((item, i) => (
          <button
            key={i}
            className={`px-2 py-1 text-sm rounded ${
              item === '|' 
                ? 'text-neutral-300 dark:text-neutral-600' 
                : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
            }`}
          >
            {item}
          </button>
        ))}
      </div>
      
      {/* Editor Content */}
      <div className="p-4 space-y-4 flex-1 flex flex-col">
        {/* Video embed placeholder */}
        <div className="aspect-video bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <Video className="w-12 h-12 text-neutral-400 mx-auto mb-2" />
            <span className="text-sm text-neutral-500">Drop video here or click to upload</span>
          </div>
        </div>
        
        {/* Text content */}
        <div className="space-y-3">
          <div className="h-6 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4" />
          <div className="h-4 bg-neutral-100 dark:bg-neutral-800 rounded w-full" />
          <div className="h-4 bg-neutral-100 dark:bg-neutral-800 rounded w-5/6" />
          <div className="h-4 bg-neutral-100 dark:bg-neutral-800 rounded w-4/5" />
        </div>
        
        {/* Blinking cursor */}
        <div className="flex items-center gap-1">
          <div className="w-0.5 h-5 bg-emerald-500 animate-pulse" />
          <span className="text-sm text-neutral-400">Start typing...</span>
        </div>
      </div>
    </motion.div>
  );
}

const features = [
  {
    icon: Video,
    title: "Flexible Content",
    description: "Video, text, and downloadable files in any lesson",
  },
  {
    icon: Layers,
    title: "Organized Structure",
    description: "Sections and lessons keep content organized",
  },
  {
    icon: Eye,
    title: "Student Preview",
    description: "See exactly what students will experience",
  },
];

export function CourseBuilderShowcase() {
  return (
    <section className="py-16 md:py-24 lg:py-32 bg-neutral-50 dark:bg-neutral-950/50">
      <Container>
        {/* Two-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Left: Curriculum Builder */}
          <div className="border-b md:border-b-0 md:border-r border-neutral-200 dark:border-neutral-800 pb-6 md:pb-0 md:pr-6 flex flex-col">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="mb-6"
            >
              <h3 className="text-xl font-bold text-foreground mb-2">Drag-and-Drop Builder</h3>
              <p className="text-muted-foreground">
                Create sections, reorder lessons, and build your curriculum with an intuitive interface.
              </p>
            </motion.div>
            <div className="flex-1 flex flex-col">
              <CurriculumBuilderMockup />
            </div>
          </div>
          
          {/* Right: Lesson Editor */}
          <div className="pt-6 md:pt-0 md:pl-6 flex flex-col">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="mb-6"
            >
              <h3 className="text-xl font-bold text-foreground mb-2">Rich Content Editor</h3>
              <p className="text-muted-foreground">
                Embed videos, add formatted text, and upload files — all in one powerful editor.
              </p>
            </motion.div>
            <div className="flex-1 flex flex-col">
              <LessonEditorMockup />
            </div>
          </div>
        </div>

        {/* Bottom feature row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8 border-t border-neutral-200 dark:border-neutral-800">
          {features.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.4 }}
              viewport={{ once: true }}
              className="flex items-start gap-3"
            >
              <div className="w-10 h-10 rounded-lg bg-neutral-200 dark:bg-neutral-800 flex items-center justify-center flex-shrink-0">
                <feature.icon className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-1">{feature.title}</h4>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}

